import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { format, parseISO } from 'date-fns';

var STATUS_CFG = {
  new: { label: 'New', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300' },
  assigned: { label: 'Assigned', cls: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300' },
  in_progress: { label: 'In Progress', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300' },
  demo_scheduled: { label: 'Demo Scheduled', cls: 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' },
  poc_in_progress: { label: 'POC', cls: 'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300' },
  completed: { label: 'Completed', cls: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' },
  failed: { label: 'Failed', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' },
  cancelled: { label: 'Cancelled', cls: 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400' },
};

var REQ_TYPES = [
  { value: 'demo', label: 'Product Demo' },
  { value: 'poc', label: 'Proof of Concept' },
  { value: 'technical_evaluation', label: 'Technical Evaluation' },
  { value: 'solution_design', label: 'Solution Design' },
  { value: 'integration_assessment', label: 'Integration Assessment' },
];

var ACTIVITY_ICONS = {
  created: 'M12 4v16m8-8H4',
  status_change: 'M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15',
  assigned: 'M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z',
  note: 'M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z',
  demo_given: 'M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z',
  poc_update: 'M9 3v2m6-2v2M9 19v2m6-2v2M5 9H3m2 6H3m18-6h-2m2 6h-2M7 19h10a2 2 0 002-2V7a2 2 0 00-2-2H7a2 2 0 00-2 2v10a2 2 0 002 2z',
  technical_call: 'M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z',
  evaluation: 'M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z',
};

export default function PreSalesTab(props) {
  var leadId = props.leadId;
  var leadName = props.leadName;
  var [requests, setRequests] = useState([]);
  var [loading, setLoading] = useState(true);
  var [showCreate, setShowCreate] = useState(false);
  var [expandedId, setExpandedId] = useState(null);
  var [noteText, setNoteText] = useState('');
  var [noteType, setNoteType] = useState('note');
  var [creating, setCreating] = useState(false);
  var [templates, setTemplates] = useState([]);
  var [form, setForm] = useState({ request_type: 'demo', title: '', requirements: '', urgency: 'medium', scheduled_date: '' });

  var fetchRequests = useCallback(function() {
    return axios.get(API + '/presales?lead_id=' + leadId + '&limit=50').then(function(r) {
      setRequests(r.data.data || []);
    }).catch(function() {}).finally(function() { setLoading(false); });
  }, [leadId]);

  var fetchTemplates = useCallback(function() {
    return axios.get(API + '/presales/templates').then(function(r) { setTemplates(r.data || []); }).catch(function() {});
  }, []);

  useEffect(function() { fetchRequests(); fetchTemplates(); }, [fetchRequests, fetchTemplates]);

  function handleCreate() {
    if (!form.title.trim()) return;
    setCreating(true);
    var payload = { lead_id: leadId, request_type: form.request_type, title: form.title, requirements: form.requirements, urgency: form.urgency };
    if (form.scheduled_date) payload.scheduled_date = form.scheduled_date;
    axios.post(API + '/presales', payload).then(function() {
      setShowCreate(false);
      setForm({ request_type: 'demo', title: '', requirements: '', urgency: 'medium', scheduled_date: '' });
      fetchRequests();
    }).catch(function() {}).finally(function() { setCreating(false); });
  }

  function handleApplyTemplate(tplId) {
    var tpl = templates.find(function(t) { return t.id === tplId; });
    if (tpl) {
      // Only overwrite fields the user hasn't already edited
      setForm(function(prev) {
        return {
          ...prev,
          title: prev.title ? prev.title : tpl.title_template,
          requirements: prev.requirements ? prev.requirements : tpl.requirements_template,
          request_type: tpl.request_type || prev.request_type,
          urgency: tpl.urgency || prev.urgency,
        };
      });
    }
  }

  function handleAddNote(reqId) {
    if (!noteText.trim()) return;
    axios.post(API + '/presales/' + reqId + '/activities', { note: noteText, activity_type: noteType }).then(function() {
      setNoteText('');
      setNoteType('note');
      fetchRequests();
    }).catch(function() {});
  }

  function handleUpdateField(reqId, field, value) {
    var body = {};
    body[field] = value;
    axios.put(API + '/presales/' + reqId, body).then(function() { fetchRequests(); }).catch(function() {});
  }

  function fmtDate(d) {
    if (!d) return '—';
    try { return format(parseISO(d), 'MMM d, yyyy HH:mm'); } catch (e) { return d; }
  }

  if (loading) return <div className="flex items-center justify-center py-12"><div className="w-6 h-6 border-2 border-slate-200 border-t-slate-600 rounded-full animate-spin"></div></div>;

  return (
    <div className="space-y-4" data-testid="presales-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Pre-Sales Requests ({requests.length})</h3>
        <button onClick={function() { setShowCreate(!showCreate); }} className="px-3 py-1.5 text-sm font-medium rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 hover:bg-slate-800 dark:hover:bg-white" data-testid="create-presales-btn">
          {showCreate ? 'Cancel' : '+ Request Pre-Sales'}
        </button>
      </div>

      {showCreate && (
        <div className="bg-slate-50 dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl p-4 space-y-3" data-testid="create-presales-form">
          {templates.length > 0 && (
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Quick Template</label>
              <select onChange={function(e) { if (e.target.value) handleApplyTemplate(e.target.value); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="template-select">
                <option value="">Choose a template...</option>
                {templates.map(function(t) { return <option key={t.id} value={t.id}>{t.name}</option>; })}
              </select>
            </div>
          )}
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Type</label>
              <select value={form.request_type} onChange={function(e) { setForm({...form, request_type: e.target.value}); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="presales-type-select">
                {REQ_TYPES.map(function(t) { return <option key={t.value} value={t.value}>{t.label}</option>; })}
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Urgency</label>
              <select value={form.urgency} onChange={function(e) { setForm({...form, urgency: e.target.value}); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="presales-urgency-select">
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Title</label>
            <input type="text" value={form.title} onChange={function(e) { setForm({...form, title: e.target.value}); }} placeholder="e.g., Product Demo for Contact Center" className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="presales-title-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Requirements</label>
            <textarea value={form.requirements} onChange={function(e) { setForm({...form, requirements: e.target.value}); }} rows={3} placeholder="Describe what the customer needs..." className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="presales-requirements-input" />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1">Scheduled Date (optional)</label>
            <input type="datetime-local" value={form.scheduled_date} onChange={function(e) { setForm({...form, scheduled_date: e.target.value}); }} className="w-full px-3 py-2 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid="presales-scheduled-date" />
          </div>
          <button onClick={handleCreate} disabled={creating || !form.title.trim()} className="px-4 py-2 text-sm font-medium rounded-lg bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 disabled:opacity-50" data-testid="submit-presales-btn">{creating ? 'Creating...' : 'Submit Request'}</button>
        </div>
      )}

      {requests.length === 0 && !showCreate && (
        <div className="text-center py-10 text-slate-400 dark:text-slate-500">
          <svg className="w-10 h-10 mx-auto mb-2 opacity-50" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" /></svg>
          <p className="text-sm">No pre-sales requests for this lead</p>
          <p className="text-xs mt-1">Click "+ Request Pre-Sales" to create one</p>
        </div>
      )}

      {requests.map(function(req) {
        var sc = STATUS_CFG[req.status] || STATUS_CFG.new;
        var isExpanded = expandedId === req.id;
        return (
          <div key={req.id} className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-xl overflow-hidden" data-testid={'presales-request-' + req.id}>
            <div className="p-4 cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50" onClick={function() { setExpandedId(isExpanded ? null : req.id); }}>
              <div className="flex items-start justify-between gap-3">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <h4 className="text-sm font-semibold text-slate-900 dark:text-white">{req.title}</h4>
                    <span className={'text-[10px] font-medium px-1.5 py-0.5 rounded ' + sc.cls}>{sc.label}</span>
                    <span className="text-[10px] px-1.5 py-0.5 bg-slate-100 dark:bg-slate-700 text-slate-500 dark:text-slate-400 rounded">{(req.request_type || '').replace(/_/g, ' ')}</span>
                    {req.sla && req.sla.response_status === 'breached' && <span className="text-[10px] px-1.5 py-0.5 bg-red-100 dark:bg-red-900/30 text-red-600 dark:text-red-400 rounded font-medium">SLA Breached</span>}
                    {req.outcome && <span className={'text-[10px] px-1.5 py-0.5 rounded font-medium ' + (req.outcome === 'won_deal' ? 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300' : req.outcome === 'lost_deal' ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300' : 'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-400')}>{req.outcome.replace(/_/g, ' ')}</span>}
                  </div>
                  <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">
                    By {req.requested_by_name} {'\u00B7'} {fmtDate(req.created_at)}
                    {req.assigned_to_name && <span> {'\u00B7'} Assigned to <span className="text-slate-700 dark:text-slate-200 font-medium">{req.assigned_to_name}</span></span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {req.technical_score != null && (
                    <span className={'text-sm font-bold ' + (req.technical_score >= 70 ? 'text-green-600' : req.technical_score >= 40 ? 'text-amber-600' : 'text-red-600')}>{req.technical_score}/100</span>
                  )}
                  <svg className={'w-4 h-4 text-slate-400 transition-transform ' + (isExpanded ? 'rotate-180' : '')} fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
                </div>
              </div>
            </div>

            {isExpanded && (
              <div className="border-t border-slate-200 dark:border-slate-700 p-4 space-y-4">
                {req.requirements && (
                  <div>
                    <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Requirements</p>
                    <p className="text-sm text-slate-700 dark:text-slate-300 whitespace-pre-wrap">{req.requirements}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase">Status</label>
                    <select value={req.status} onChange={function(e) { handleUpdateField(req.id, 'status', e.target.value); }} className="w-full mt-1 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300" data-testid={'detail-status-' + req.id}>
                      {Object.keys(STATUS_CFG).map(function(k) { return <option key={k} value={k}>{STATUS_CFG[k].label}</option>; })}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase">Technical Score</label>
                    <input type="number" min={0} max={100} value={req.technical_score || ''} onChange={function(e) { handleUpdateField(req.id, 'technical_score', parseInt(e.target.value) || 0); }} className="w-full mt-1 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300" placeholder="0-100" data-testid={'tech-score-' + req.id} />
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase">POC Status</label>
                    <select value={req.poc_status || ''} onChange={function(e) { handleUpdateField(req.id, 'poc_status', e.target.value); }} className="w-full mt-1 px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300" data-testid={'poc-status-' + req.id}>
                      <option value="">N/A</option>
                      <option value="not_started">Not Started</option>
                      <option value="in_progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="failed">Failed</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] font-medium text-slate-500 dark:text-slate-400 uppercase">Urgency</label>
                    <p className={'text-sm font-medium mt-1 uppercase ' + (req.urgency === 'critical' ? 'text-red-600' : req.urgency === 'high' ? 'text-orange-600' : 'text-slate-600 dark:text-slate-300')}>{req.urgency}</p>
                  </div>
                </div>

                {req.solution_notes && <div><p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Solution Notes</p><p className="text-sm text-slate-700 dark:text-slate-300">{req.solution_notes}</p></div>}
                {req.competitor_info && <div><p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-1">Competitor Info</p><p className="text-sm text-slate-700 dark:text-slate-300">{req.competitor_info}</p></div>}

                <div>
                  <p className="text-xs font-medium text-slate-500 dark:text-slate-400 mb-2">Activity Timeline</p>
                  <div className="space-y-2 max-h-[250px] overflow-y-auto">
                    {(req.activities || []).slice().reverse().map(function(act) {
                      var iconPath = ACTIVITY_ICONS[act.activity_type] || ACTIVITY_ICONS.note;
                      return (
                        <div key={act.id} className="flex items-start gap-2 text-xs">
                          <svg className="w-4 h-4 text-slate-400 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={iconPath} /></svg>
                          <div>
                            <span className="font-medium text-slate-700 dark:text-slate-200">{act.user_name}</span>
                            <span className="text-slate-500 dark:text-slate-400"> {'\u00B7'} {fmtDate(act.created_at)}</span>
                            <p className="text-slate-600 dark:text-slate-300 mt-0.5">{act.note}</p>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>

                <div className="flex gap-2">
                  <select value={noteType} onChange={function(e) { setNoteType(e.target.value); }} className="px-2 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-300">
                    <option value="note">Note</option>
                    <option value="demo_given">Demo Given</option>
                    <option value="poc_update">POC Update</option>
                    <option value="technical_call">Technical Call</option>
                    <option value="evaluation">Evaluation</option>
                  </select>
                  <input type="text" value={noteText} onChange={function(e) { setNoteText(e.target.value); }} placeholder="Add a note..." className="flex-1 px-3 py-1.5 text-xs border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-white" data-testid={'note-input-' + req.id} onKeyDown={function(e) { if (e.key === 'Enter') handleAddNote(req.id); }} />
                  <button onClick={function() { handleAddNote(req.id); }} disabled={!noteText.trim()} className="px-3 py-1.5 text-xs font-medium bg-slate-900 dark:bg-slate-100 text-white dark:text-slate-900 rounded-lg disabled:opacity-50" data-testid={'add-note-btn-' + req.id}>Add</button>
                </div>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
